import { useState, useEffect, forwardRef } from 'react';
import { Trash2, Download, MessageSquare, MapPin, Calendar, Search, ChevronDown } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import api from '../../services/api';

const toDateStr = (d) => format(d, 'yyyy-MM-dd');
const defaultTo = new Date();
const defaultFrom = new Date(Date.now() - 30 * 86400000);

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div
    ref={ref}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(e); }}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '9px 12px', borderRadius: 10,
      border: '1.5px solid #e5e7eb', backgroundColor: '#fff',
      cursor: 'pointer', userSelect: 'none', width: '100%',
      transition: 'border-color 0.15s',
    }}
  >
    <Calendar size={15} color={value ? '#16a34a' : '#9ca3af'} style={{ flexShrink: 0 }} />
    <span style={{ fontSize: 14, fontFamily: "'Poppins', sans-serif", color: value ? '#1f2937' : '#9ca3af', flex: 1 }}>
      {value || placeholder}
    </span>
    <ChevronDown size={13} color="#9ca3af" style={{ flexShrink: 0 }} />
  </div>
));
DateInput.displayName = 'DateInput';

const ManageContacts = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = (f, t) => {
    setLoading(true);
    const params = {};
    if (f) params.from = toDateStr(f);
    if (t) params.to = toDateStr(t);
    api.get('/contact', { params })
      .then(res => setSubmissions(res.data.submissions || []))
      .catch(() => toast.error('Failed to load enquiries.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(defaultFrom, defaultTo); }, []);

  const handleFilter = () => load(from, to);
  const handleClear = () => { setFrom(defaultFrom); setTo(defaultTo); load(defaultFrom, defaultTo); };

  const doDelete = async (id) => {
    try {
      await api.delete(`/contact/${id}`);
      setSubmissions(prev => prev.filter(s => s.id !== id));
      toast.success('Enquiry deleted.');
    } catch {
      toast.error('Failed to delete.');
    }
  };

  const handleDelete = (id, name) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: '500' }}>Delete enquiry from "{name}"?</p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => { toast.dismiss(t.id); doDelete(id); }}
            style={{ padding: '6px 14px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Delete
          </button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ padding: '6px 14px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const exportExcel = () => {
    if (!filtered.length) { toast.error('No data to export.'); return; }
    const data = filtered.map(s => ({
      'Name': s.name,
      'Email': s.email,
      'Phone': s.phone || '—',
      'Subject': s.subject || '—',
      'Message': s.message,
      'Location': s.location || '—',
      'Date': fmt(s.created_at),
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [
      { wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 28 },
      { wch: 50 }, { wch: 22 }, { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Contact Enquiries');
    XLSX.writeFile(wb, `contact-enquiries-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported!');
  };

  const fmt = (d) => new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const filtered = search
    ? submissions.filter(s =>
        s.name.toLowerCase().includes(search.toLowerCase()) ||
        s.email.toLowerCase().includes(search.toLowerCase()) ||
        (s.subject || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.location || '').toLowerCase().includes(search.toLowerCase())
      )
    : submissions;

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.topBar}>
        <div>
          <h1 style={s.pageTitle}>Contact Enquiries</h1>
          <p style={s.pageSubtitle}>{submissions.length} total · {filtered.length} shown</p>
        </div>
        <button style={s.exportBtn} onClick={exportExcel}>
          <Download size={18} /> Export Excel
        </button>
      </div>

      {/* Filters */}
      <div style={s.filterCard}>
        <div style={s.filterRow}>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}><Calendar size={14} /> From</label>
            <DatePicker
              selected={from}
              onChange={(date) => setFrom(date)}
              selectsStart startDate={from} endDate={to}
              maxDate={to || new Date()}
              dateFormat="dd MMM yyyy"
              placeholderText="From date"
              customInput={<DateInput />}
            />
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}><Calendar size={14} /> To</label>
            <DatePicker
              selected={to}
              onChange={(date) => setTo(date)}
              selectsEnd startDate={from} endDate={to}
              minDate={from} maxDate={new Date()}
              dateFormat="dd MMM yyyy"
              placeholderText="To date"
              customInput={<DateInput />}
            />
          </div>
          <div style={{ ...s.filterGroup, flex: 2, minWidth: 220 }}>
            <label style={s.filterLabel}><Search size={14} /> Search</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={15} color="#9ca3af" style={{ position: 'absolute', left: 12, pointerEvents: 'none', flexShrink: 0 }} />
              <input
                style={{
                  width: '100%', padding: '9px 36px 9px 36px',
                  border: '1.5px solid #e5e7eb', borderRadius: 10,
                  fontSize: 14, fontFamily: "'Poppins', sans-serif",
                  color: '#1f2937', backgroundColor: '#fff',
                  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                placeholder="Name, email, subject…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={e => { e.target.style.borderColor = '#16a34a'; e.target.style.boxShadow = '0 0 0 3px rgba(22,163,74,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  style={{ position: 'absolute', right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center', padding: 2 }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div style={s.filterActions}>
            <button style={s.applyBtn} onClick={handleFilter}>Apply</button>
            <button style={s.clearBtn} onClick={handleClear}>Clear</button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <p style={{ color: '#6b7280', padding: '40px 0' }}>Loading enquiries...</p>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>
          <MessageSquare size={64} color="#d1d5db" />
          <p style={s.emptyText}>No enquiries found.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                {['#', 'Name', 'Email', 'Phone', 'Subject', 'Location', 'Date', ''].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((sub, idx) => (
                <>
                  <tr key={sub.id} style={{ ...s.tr, cursor: 'pointer' }}
                    onClick={() => setExpanded(expanded === sub.id ? null : sub.id)}>
                    <td style={s.td}>{idx + 1}</td>
                    <td style={{ ...s.td, fontWeight: '600', color: '#111827' }}>{sub.name}</td>
                    <td style={s.td}>
                      <a href={`mailto:${sub.email}`} style={s.link} onClick={e => e.stopPropagation()}>
                        {sub.email}
                      </a>
                    </td>
                    <td style={s.td}>{sub.phone || <span style={s.muted}>—</span>}</td>
                    <td style={s.td}>
                      {sub.subject
                        ? <span style={s.subjectBadge}>{sub.subject}</span>
                        : <span style={s.muted}>—</span>}
                    </td>
                    <td style={s.td}>
                      {sub.location
                        ? <span style={s.locationChip}><MapPin size={12} />{sub.location}</span>
                        : <span style={s.muted}>—</span>}
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap', color: '#6b7280', fontSize: '13px' }}>
                      {fmt(sub.created_at)}
                    </td>
                    <td style={s.td}>
                      <button style={s.deleteBtn}
                        onClick={e => { e.stopPropagation(); handleDelete(sub.id, sub.name); }}>
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                  {expanded === sub.id && (
                    <tr key={`${sub.id}-msg`}>
                      <td colSpan={8} style={s.expandedCell}>
                        <div style={s.messageBox}>
                          <p style={s.messageLabel}>Message</p>
                          <p style={s.messageText}>{sub.message}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '32px', backgroundColor: '#f9fafb', minHeight: '100vh' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  pageTitle: { fontSize: '32px', fontWeight: 'bold', color: '#111827', marginBottom: '6px' },
  pageSubtitle: { fontSize: '16px', color: '#6b7280' },
  exportBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },

  filterCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  filterRow: { display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '160px' },
  filterLabel: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: '600', color: '#374151' },
  filterActions: { display: 'flex', gap: '8px', alignItems: 'flex-end', paddingBottom: '1px' },
  applyBtn: { padding: '9px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  clearBtn: { padding: '9px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },

  empty: { textAlign: 'center', padding: '80px 20px', backgroundColor: 'white', borderRadius: '12px' },
  emptyText: { fontSize: '18px', color: '#9ca3af', marginTop: '16px' },

  tableWrap: { backgroundColor: 'white', borderRadius: '12px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { padding: '14px 16px', backgroundColor: '#f9fafb', color: '#374151', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' },
  td: { padding: '14px 16px', color: '#374151', verticalAlign: 'middle' },

  link: { color: '#16a34a', textDecoration: 'none', fontWeight: '500' },
  muted: { color: '#9ca3af' },
  subjectBadge: { padding: '3px 10px', backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  locationChip: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', backgroundColor: '#f0fdf4', color: '#15803d', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  deleteBtn: { padding: '6px 8px', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' },

  expandedCell: { padding: '0', backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' },
  messageBox: { padding: '16px 24px' },
  messageLabel: { fontSize: '12px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' },
  messageText: { fontSize: '15px', color: '#374151', lineHeight: '1.7', whiteSpace: 'pre-wrap' },
};

export default ManageContacts;
